import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { Platform } from 'react-native';

import type { BookingPreview, NewBookingItem } from '@/src/api/bookingsApi';
import type { EventSlot, EventSlotTicketTier } from '@/src/api/eventsApi';
import { useFetchEvent, type FetchEventState } from '@/src/hooks/useFetchEvent';

/**
 * ── Event cart: the event being booked, and the selection against it ─────
 *
 * Everything a booking-in-progress is made of, held ABOVE the two routes that
 * show it (`[id]/index` and `[id]/confirm`): the EVENT itself, the picked slot,
 * the tickets per tier, and what they add up to.
 *
 * ── Why the event lives here and not in each screen ──
 * Both routes need the same event, and they're separate screens: fetching per
 * screen means the confirm step re-requests an event the user is already looking
 * at, and going back re-requests it again. Fetching once here, keyed by the id
 * from the route, makes the trip to confirm and back FREE — and makes the two
 * screens incapable of disagreeing about the event they're drawing.
 *
 * It also puts the fetch where the booking's lifecycle already is. Today the user
 * always arrives via the detail screen, so the event is loaded before confirm
 * ever mounts and confirm's `loading` branch is only theoretical. That changes
 * with resuming an ABANDONED booking, which lands the user on confirm directly:
 * the provider mounts there, and the event (later, the server's saved cart) is
 * fetched with nothing on screen yet — so confirm has to render loading and
 * error states of its own. It has placeholders for both now; they get designed
 * with that feature.
 *
 * ── Why the totals live here ──
 * The cart stores a LINE per booked tier — the tier itself, not just a count —
 * which is what lets it add itself up: a count needs no prices, but a total does.
 * Every surface (the detail screen's booking bar, the confirm step's receipt)
 * then prints the SAME numbers instead of re-deriving them from its own copy of
 * the event.
 *
 * The clamp lives here for the same reason: the ceiling on a tier
 * (`maxSeatsPerUser`, `availableCapacity`) is a property of the TIER, which
 * `changeQuantity` is handed, so a caller can't store a quantity the tier
 * doesn't allow.
 *
 * ── Why the booking payload lives here ──
 * The cart also hands out `bookingItems` — the same selection as `POST /bookings`
 * wants it. It's the SELECTION, which is this module's own state, so building it
 * here is a projection; the confirm step used to reconstruct it from `lines`,
 * which meant reading `slotTierId` back out of a field (`CartLine.id`) that
 * exists to be a React key and print a receipt. Those two shapes want to diverge
 * — a line will grow display-only fields, and an item will grow whatever the wire
 * asks for next — so they're derived side by side from `quantities` instead of one
 * from the other.
 *
 * The detail screen still owns the DOMAIN logic (which slots are worth offering,
 * lifecycle badges, the booking-CTA fork — see `app/event/[id]/index.tsx`); it
 * reads the event and the selection from here.
 *
 * Scoped to ONE event: mounted in `app/event/[id]/_layout.tsx` with that route's
 * id, so it lives for as long as the user is inside that event (detail ⇄
 * confirm) and is fresh on the next one. `selectedSlot` is null until the detail
 * screen defaults it (to the first bookable slot).
 */

/**
 * ── Surviving a refresh, on web only ──────────────────────────────────────
 *
 * The cart above lives in React state, which a WEB page reload throws away —
 * unlike native, which has no "refresh" to survive in the first place.
 * `confirm.tsx` reads this context assuming the detail screen already
 * populated it, so a reload on either route currently mounts this provider
 * fresh with nothing to read.
 *
 * The fix mirrors the selection into `localStorage`, keyed by `eventId`, and
 * reads it back SYNCHRONOUSLY in the `useState` initializers below — not in
 * an effect. An effect would run too late: mount effects fire children-first,
 * so the detail screen's own effect (defaulting `selectedSlot` the instant
 * it's null — see `app/events/[id]/index.tsx`) would run BEFORE a parent's
 * hydration effect ever got a chance, locking in a default slot and wiping
 * the very quantities being restored (`selectSlot` empties them). Reading
 * during the initializer instead means the state is already correct before
 * any child so much as renders once — render order is parent-first, with no
 * such hazard.
 *
 * Only the SELECTION is persisted (`slotTierId` → quantity), never a tier
 * snapshot: prices, names and availability are looked up fresh off
 * `selectedSlot` wherever the cart is added up (see the `lines` derivation
 * below), so a cart restored after a refresh can't show yesterday's price.
 *
 * Lifecycle: written on every change while this provider is mounted, and
 * removed the moment it unmounts — leaving this event's routes for a
 * different one, or out of the flow entirely (including a completed
 * booking, which navigates out of `/events/[id]` altogether). That's the
 * SAME "gone when the user leaves it" lifetime `_layout.tsx` already gives
 * the in-memory cart (see its own docstring) — this is that lifetime,
 * mirrored. A plain reload does not run that cleanup: the whole page is
 * discarded before the next one mounts, which is exactly the gap this
 * exists to survive.
 *
 * Guarded twice, not once: `Platform.OS === 'web'` opts native out (nothing
 * to survive there — see the module docstring), and
 * `typeof window === 'undefined'` opts the SERVER render out — this module
 * runs there too (`web.output: 'server'`, see `generateMetadata` in the
 * detail screen), where `window` doesn't exist at all. The client's own
 * first render (hydration) still calls these normally and picks up whatever
 * is on disk — same as `sessionStore`'s user, null on the server, real once
 * hydrated — so this is one more instance of an already-accepted pattern,
 * not a new one.
 */
type PersistedCart = {
  selectedSlotId: string | null;
  quantities: Record<string, number>;
  socialLink: string | null;
  couponCode: string | null;
};

function cartStorageKey(eventId: string): string {
  return `eventCart:${eventId}`;
}

function readPersistedCart(eventId: string): PersistedCart | null {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(cartStorageKey(eventId));
    return raw ? (JSON.parse(raw) as PersistedCart) : null;
  } catch {
    // Corrupt JSON, storage disabled — starts empty, same as a first visit.
    return null;
  }
}

function writePersistedCart(eventId: string, cart: PersistedCart): void {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(cartStorageKey(eventId), JSON.stringify(cart));
  } catch {
    // Full, or blocked (Safari private mode) — losing the refresh-survival
    // copy isn't a booking failure, so this fails silently.
  }
}

function clearPersistedCart(eventId: string): void {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(cartStorageKey(eventId));
  } catch {
    // ignore
  }
}

/** One booked tier, as the receipt renders it. `id` is the tier's `slotTierId`. */
export type CartLine = {
  id: string;
  name: string;
  quantity: number;
  /** unit price × quantity, formatted — "Free" at zero. */
  subtotalDisplay: string;
};

type EventCartValue = {
  /** The event being booked — loading / error / the event itself. */
  eventState: FetchEventState;
  /** The picked slot, or null before the detail screen defaults it. */
  selectedSlot: EventSlot | null;
  /** Pick a slot. EMPTIES the cart — the tiers on sale differ per slot. */
  selectSlot: (slotId: string) => void;
  /** How many of a tier are in the cart (never in it ⇒ 0). */
  quantityOf: (slotTierId: string) => number;
  /** Add/remove tickets of a tier; the result is clamped to what it allows. */
  changeQuantity: (tier: EventSlotTicketTier, delta: number) => void;
  /** One line per booked tier, in the order the tiers were first added. */
  lines: CartLine[];
  /**
   * The cart as `POST /bookings` takes it — one `{slotTierId, quantity}` per
   * booked tier. Empty cart ⇒ empty array, which is not a bookable request:
   * the caller checks it rather than sending nothing.
   */
  bookingItems: NewBookingItem[];
  /** Tickets in the cart, across every tier. */
  totalTickets: number;
  /** The tickets' price before any coupon — "Free" at zero. */
  subtotalDisplay: string;
  /** Nothing to discount: a coupon has no business on this cart. */
  isSubtotalFree: boolean;
  /** What's owed: the priced coupon's amount due, else the subtotal. */
  totalDisplay: string;
  /** Nothing to pay: the CTA confirms instead of collecting money. */
  isFree: boolean;
  /**
   * The approval-only tier on the selected slot, if it's bookable — derived
   * live off `selectedSlot`, not stored (see its definition below). Null on
   * every event without one, per the standing assumption that at most one such
   * tier exists and, when it does, it's the event's only tier. The screen
   * reads `requiresSocialLink` off it to decide whether the social-link sheet
   * stands between "Book now" and confirm.
   */
  approvalTier: EventSlotTicketTier | null;
  /** The social link collected for `approvalTier`, or null before it's given. */
  socialLink: string | null;
  setSocialLink: (link: string) => void;
  // The code outlives a cart change; its quote doesn't — it was priced for
  // one exact selection, so any change drops it until confirm re-prices.
  couponCode: string | null;
  couponQuote: BookingPreview | null;
  applyCoupon: (code: string, quote: BookingPreview) => void;
  removeCoupon: () => void;
};

const EventCartContext = createContext<EventCartValue | null>(null);

/**
 * Owns the event + cart for one event's routes. Mount in
 * `app/event/[id]/_layout.tsx`, passing that route's id, so the detail screen
 * and the confirm step share one fetch and one selection.
 */
export function EventCartProvider({
  eventId,
  children,
}: {
  eventId: string;
  children: ReactNode;
}) {
  const eventState = useFetchEvent(eventId);

  // Read once, synchronously, on the first render of THIS mount — see the
  // persistence block above for why it has to happen here and not in an
  // effect. `useState`'s own lazy-initializer form is what makes it once:
  // called only on mount, never on a later re-render.
  const [persisted] = useState(() => readPersistedCart(eventId));

  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(
    persisted?.selectedSlotId ?? null
  );
  // Keyed by `slotTierId` so a lookup is O(1) and a tier can't appear twice. A
  // tier at 0 is REMOVED rather than kept as a zero line, so the map is exactly
  // "what's in the cart" and the derivations below need no filtering. Values
  // are plain quantities, not tier snapshots — see the persistence block
  // above for why the tier itself is always looked up fresh instead.
  const [quantities, setQuantities] = useState<Record<string, number>>(
    persisted?.quantities ?? {}
  );
  const [socialLink, setSocialLink] = useState<string | null>(
    persisted?.socialLink ?? null
  );
  const [couponCode, setCouponCode] = useState<string | null>(
    persisted?.couponCode ?? null
  );
  const [couponQuote, setCouponQuote] = useState<BookingPreview | null>(null);

  const applyCoupon = useCallback((code: string, quote: BookingPreview) => {
    setCouponCode(code);
    setCouponQuote(quote);
  }, []);
  const removeCoupon = useCallback(() => {
    setCouponCode(null);
    setCouponQuote(null);
  }, []);

  // Mirror every change back to storage — see the persistence block above
  // for the lifecycle this is meant to have.
  useEffect(() => {
    writePersistedCart(eventId, {
      selectedSlotId,
      quantities,
      socialLink,
      couponCode,
    });
  }, [eventId, selectedSlotId, quantities, socialLink, couponCode]);

  // ...and drop it the moment this provider is gone for good. Scoped to
  // `eventId` (not re-run on every render) since it's the mount/unmount pair
  // that marks the cart's real lifetime, not any one render of it.
  useEffect(() => {
    return () => clearPersistedCart(eventId);
  }, [eventId]);

  // Resolved against the fetched event, so a slot id that outlives its event (a
  // new fetch, a slot that's gone) simply reads as "nothing picked" and the
  // detail screen defaults it again. Memoized so its identity only changes when
  // the slot actually does — `approvalTier` below is derived off it.
  const selectedSlot = useMemo(
    () =>
      eventState.event?.slots.find((slot) => slot.id === selectedSlotId) ??
      null,
    [eventState.event, selectedSlotId]
  );

  const selectSlot = useCallback((slotId: string) => {
    setSelectedSlotId(slotId);
    setQuantities({}); // tiers differ per slot; a stale cart wouldn't map cleanly
    setSocialLink(null); // it answered for the OLD slot's tier, not the new one
    setCouponQuote(null);
  }, []);

  // Approval-only tiers cap out at one seat and skip the stepper entirely (see
  // TicketTier), so there's no "+ Add" tap to ever drive a `quantities` entry
  // for one — picking the slot IS the request. Rather than mirror that into
  // `quantities` via an effect (React flags this: it's state derivable at
  // render time, and setState-from-effect just adds a lagged, cascading
  // render), it's derived straight from `selectedSlot` on every render. That
  // also means it tracks a bookability flip with no slot reselection needed —
  // e.g. pull-to-refresh picking up a login or an approval elsewhere.
  const approvalTier = useMemo(
    () =>
      selectedSlot?.ticketTiers.find(
        (t) => t.requiresGuestApproval && t.bookability.isBookable
      ) ?? null,
    [selectedSlot]
  );

  const quantityOf = useCallback(
    (slotTierId: string) =>
      quantities[slotTierId] ??
      (approvalTier?.slotTierId === slotTierId ? 1 : 0),
    [quantities, approvalTier]
  );

  const changeQuantity = useCallback(
    (tier: EventSlotTicketTier, delta: number) => {
      setQuantities((prev) => {
        // Both ceilings apply: the per-user cap AND what's actually left.
        const quantity = Math.max(
          0,
          Math.min(
            tier.maxSeatsPerUser,
            tier.availableCapacity,
            (prev[tier.slotTierId] ?? 0) + delta
          )
        );
        const next = { ...prev };
        if (quantity === 0) delete next[tier.slotTierId];
        else next[tier.slotTierId] = quantity;
        return next;
      });
      setCouponQuote(null);
    },
    []
  );

  // One pass over the cart for everything derived from it: the receipt lines,
  // the request items, and the two totals. They're all the same walk, and
  // deriving them together is what stops a surface from disagreeing with the
  // payload. `approvalTier` is folded in as one more line at quantity 1 — it's
  // never IN `quantities` (see `approvalTier` above), so it'd otherwise be
  // silently missing from the receipt and the booking payload.
  //
  // The tier for each entry is looked up fresh off `selectedSlot` rather than
  // carried inside `quantities` — that's what lets a cart restored from
  // `localStorage` (see the persistence block above `CartLine`) hold nothing
  // but an id and a count, yet still print today's price. A `slotTierId` with
  // no match on the current slot (removed, or a rehydrated id from a slot
  // that no longer exists) is dropped rather than crashing the receipt.
  const { lines, bookingItems, totalTickets, totalMinorUnits } = useMemo(() => {
    const built: CartLine[] = [];
    const items: NewBookingItem[] = [];
    let tickets = 0;
    let minorUnits = 0;
    for (const [slotTierId, quantity] of Object.entries(quantities)) {
      const tier = selectedSlot?.ticketTiers.find(
        (t) => t.slotTierId === slotTierId
      );
      if (!tier) continue;
      const subtotal = quantity * tier.priceWithTax.amountInMinorUnits;
      tickets += quantity;
      minorUnits += subtotal;
      built.push({
        id: tier.slotTierId,
        name: tier.name,
        quantity,
        subtotalDisplay: formatAmount(subtotal),
      });
      items.push({ slotTierId: tier.slotTierId, quantity });
    }
    if (approvalTier) {
      const subtotal = approvalTier.priceWithTax.amountInMinorUnits;
      tickets += 1;
      minorUnits += subtotal;
      built.push({
        id: approvalTier.slotTierId,
        name: approvalTier.name,
        quantity: 1,
        subtotalDisplay: formatAmount(subtotal),
      });
      items.push({ slotTierId: approvalTier.slotTierId, quantity: 1 });
    }
    return {
      lines: built,
      bookingItems: items,
      totalTickets: tickets,
      totalMinorUnits: minorUnits,
    };
  }, [quantities, approvalTier, selectedSlot]);

  const dueMinorUnits =
    couponQuote?.amountDue.amountInMinorUnits ?? totalMinorUnits;

  const value = useMemo<EventCartValue>(
    () => ({
      eventState,
      selectedSlot,
      selectSlot,
      quantityOf,
      changeQuantity,
      lines,
      bookingItems,
      totalTickets,
      subtotalDisplay: formatAmount(totalMinorUnits),
      isSubtotalFree: totalMinorUnits === 0,
      totalDisplay: formatAmount(dueMinorUnits),
      isFree: dueMinorUnits === 0,
      approvalTier,
      socialLink,
      setSocialLink,
      couponCode,
      couponQuote,
      applyCoupon,
      removeCoupon,
    }),
    [
      eventState,
      selectedSlot,
      selectSlot,
      quantityOf,
      changeQuantity,
      lines,
      bookingItems,
      totalTickets,
      totalMinorUnits,
      dueMinorUnits,
      approvalTier,
      socialLink,
      couponCode,
      couponQuote,
      applyCoupon,
      removeCoupon,
    ]
  );

  return (
    <EventCartContext.Provider value={value}>
      {children}
    </EventCartContext.Provider>
  );
}

/**
 * An amount the app worked out, as a string.
 *
 * A single price arrives from the wire ALREADY formatted (`PSPrice.display`),
 * which is why no screen formats one — but a line subtotal and a cart total are
 * SUMS, with no server-sent string, so this is the one place the app builds one.
 * It follows the rule the tier cards print by hand: `0` reads "Free", never
 * "₹ 0". Amounts are in MINOR units (paise), hence the /100.
 *
 * Fraction digits are 0–2, not the currency default of exactly 2, so a whole
 * amount reads "₹2,400" like the wire's own `display` (a bar reading "₹2,400.00"
 * beside tier cards reading "₹2,400" looks like a different number), while a
 * fractional one still shows its paise instead of being rounded away.
 */
function formatAmount(amountInMinorUnits: number): string {
  if (amountInMinorUnits === 0) return 'Free';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amountInMinorUnits / 100);
}

export function useEventCart(): EventCartValue {
  const ctx = useContext(EventCartContext);
  if (!ctx) {
    throw new Error(
      'useEventCart must be used inside <EventCartProvider> (mounted in app/event/[id]/_layout.tsx).'
    );
  }
  return ctx;
}
